import React, { useEffect, useState } from "react";
import config from "../../modules/config";
import axios from "axios";
import { Box, Button, Divider, Typography } from "@mui/material";
import { useNavigate } from "react-router";
import { colors } from "../../constants/site-content";
import DataLoaderProgressBar from "../data-loader-progress-bar/DataLoaderProgressBar";
import { InteractiveModelProperties } from "../model/InteractiveDrawingModel/InteractiveDrawingModel";
import Overlay from "../overlay/Overlay";
import {
  setIsDrawingStarted,
  setTimer,
  setTimerDone,
} from "../../state/actions";
import { connect } from "react-redux";
import { HOME_DRAWING_GIVEN_TIME } from "../../templates/home";

export const getDistinctCategories = (data: any) => {
  const categories = new Set();
  data.forEach((item: any) => {
    categories.add(item.category);
  });
  return Array.from(categories);
};

type Props = {
  closeOverlay: () => void;
  setShouldResetCanvas: (bool: boolean) => void;
  setInteractiveModelProperties: (
    properties: InteractiveModelProperties
  ) => void;
};

const ModelSelector = ({
  closeOverlay,
  setInteractiveModelProperties,
  setShouldResetCanvas,
  ...props
}: Props & any) => {
  const [isUnauthorized, setIsUnauthorized] = useState(false);
  const navigate = useNavigate();
  const [models, setModels] = useState<any[]>([]);
  const [distinctCategories, setDistinctCategories] = useState<any>([]);
  const [loader, setLoader] = useState(true);

  useEffect(() => {
    let authorizedHeader = config.AuthorizationHeader();

    axios
      .get(
        `${process.env.REACT_APP_CROQEE_SERVER_URL}/models/getAllThreeDModels`,
        authorizedHeader
      )
      .then((res: any) => {
        res.data?.threeDModels?.data && setModels(res.data.threeDModels.data);
        setIsUnauthorized(true);
        setLoader(false);
      })
      .catch(() => {
        setIsUnauthorized(false);
        setLoader(false);
      });
  }, []);

  useEffect(() => {
    if (models.length > 0) {
      setDistinctCategories(getDistinctCategories(models));
    }
  }, [models]);

  return (
    <>
      <Overlay closeOverlay={closeOverlay}>
        {loader ? (
          <DataLoaderProgressBar />
        ) : (
          <>
            <Box
              id="model-selector"
              sx={{
                overflowY: "scroll",
                height: "auto",
                "&::-webkit-scrollbar": {
                  width: "14px",
                  backgroundColor: colors.colorNeutral0,
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: colors.colorNeutral800,
                  border: "2px solid",
                  borderColor: colors.colorNeutral0,
                  borderRadius: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: colors.colorNeutral0,
                  boxShadow: "inset 0 0 6px rgba(0,0,0,0.3)",
                },
              }}
            >
              {isUnauthorized && (
                <Box
                  sx={{
                    color: "white",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    fontSize: "20px",
                    flexDirection: "column",
                    gap: "16px",
                  }}
                >
                  {distinctCategories.map((category, index) => (
                    <Box
                      key={index}
                      width={"100%"}
                      sx={{ textAlign: "center" }}
                    >
                      {index !== 0 && <Divider sx={{ borderColor: "#fff" }} />}
                      <Typography variant="h3" sx={{ marginTop: "48px" }}>
                        {category}
                      </Typography>
                      <Box sx={{ padding: "48px" }}>
                        {Object.values(
                          models
                            .filter((model) => model.category === category)
                            .reduce((acc, model) => {
                              acc[model.section] = acc[model.section] || [];
                              acc[model.section].push(model);
                              return acc;
                            }, {})
                        ).map((sectionModels: any[], index) => (
                          <Box key={index}>
                            <Typography variant="h4" sx={{ padding: "32px" }}>
                              {sectionModels[0].section}
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "row",
                                flexWrap: "wrap",
                                gap: "16px",
                                justifyContent: "center",
                              }}
                            >
                              {sectionModels.map((model, modelIndex) => (
                                <Box key={modelIndex}>
                                  <Box
                                    onClick={() => {
                                      setInteractiveModelProperties({
                                        modelName: model.model,
                                        modelUrl: model.threeDModelUrl,
                                        hasTexture: !!model.hasTexture,
                                        cameraPosition: model.cameraPosition,
                                        modelScale: model.modelScale,
                                        controlTarget: model.controlTarget,
                                        outLineSpec: model.outLineSpec,
                                        overLineColor: model.overLineColor,
                                      });
                                      closeOverlay();
                                      props.setTimerDone(true);
                                      props.setTimer({
                                        showTimer: false,
                                        timer: HOME_DRAWING_GIVEN_TIME,
                                      });
                                      props.setIsDrawingStarted(false);
                                      setShouldResetCanvas(true);
                                    }}
                                    sx={{
                                      width: "300px",
                                      height: "300px",
                                      "@media (max-width: 768px)": {
                                        width: "200px",
                                        height: "200px",
                                      },
                                      border: `1px solid ${colors.brandColor}`,
                                      backgroundImage: `url(${model.imgUrl})`,
                                      backgroundSize: "cover",
                                      backgroundColor: "transparent",
                                      cursor: "pointer",
                                    }}
                                  ></Box>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
              {!isUnauthorized && (
                <Box
                  sx={{
                    position: "absolute",
                    color: "white",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "100%",
                    height: "100%",
                    fontSize: "20px",
                    flexDirection: "column",
                    gap: "16px",
                  }}
                >
                  <Typography sx={{ fontSize: "24px" }}>
                    Sign in to choose between more models
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ backgroundColor: colors.brandColor, height: "45px" }}
                    onClick={() => navigate("login")}
                  >
                    Sign in
                  </Button>
                  or
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ backgroundColor: colors.brandColor, height: "45px" }}
                    onClick={() => navigate("signup")}
                  >
                    Create a new account
                  </Button>
                </Box>
              )}
            </Box>
          </>
        )}
      </Overlay>
    </>
  );
};

const mapStateToProps = (state: any) => {
  // eslint-disable-next-line no-empty-pattern
  const {} = state;
  return {};
};
const mapDispatchToProps = (dispatch: any) => {
  return {
    setTimer: (payload: any) => dispatch(setTimer(payload)),
    setTimerDone: (payload: any) => dispatch(setTimerDone(payload)),
    setIsDrawingStarted: (payload: any) =>
      dispatch(setIsDrawingStarted(payload)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ModelSelector);
